import React, { ComponentType, LazyExoticComponent } from "react";

/**
 * Wraps dynamic component imports with automatic chunk retry logic.
 * If a chunk fails to load due to deployment hash changes or network hiccups,
 * it retries downloading the chunk or reloads the page once.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  chunkKey?: string
): LazyExoticComponent<T> {
  return React.lazy(async () => {
    const storageKey = `retry_chunk_${chunkKey || "route"}`;
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem(storageKey) || "false"
    );

    try {
      const component = await componentImport();
      window.sessionStorage.setItem(storageKey, "false");
      return component;
    } catch (error: any) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Assume chunk hash changed on server, force refresh to fetch new index.html
        window.sessionStorage.setItem(storageKey, "true");
        window.location.reload();
        // Return a promise that never resolves while page reloads
        return new Promise(() => {});
      }

      // If page was already refreshed, reset and throw the error to be caught by ErrorBoundary
      window.sessionStorage.setItem(storageKey, "false");
      throw error;
    }
  });
}

export default lazyWithRetry;
